
import { useState, useEffect, useCallback, useRef } from 'react';
import { AdminState } from '@/types/adminTypes';
import { loadAdminState, updateAdminState } from '@/services/adminStateService';

/**
 * Hook for handling admin state synchronization - with significantly reduced Supabase calls
 */
export const useAdminStateSync = (pollingInterval = 240000) => { // Increased to 4 minutes (from 2 minutes)
  const [adminState, setAdminState] = useState<AdminState>({
    lastActivity: new Date().toISOString(),
    activeAdmins: [],
    lastSync: new Date().toISOString()
  });
  
  // Track when we last loaded state
  const lastStateLoad = useRef(0);
  const MIN_LOAD_INTERVAL = 180000; // 3 minutes between loads (increased from 30 seconds)
  
  // Add flag to track if initial load was performed
  const initialLoadPerformed = useRef(false);
  // Add flag to track if polling is active
  const pollingActive = useRef(false);
  // Add ref for polling timer
  const pollingTimer = useRef<NodeJS.Timeout | null>(null);
  
  // Load admin state
  const loadState = useCallback(async () => {
    try {
      // Don't load too frequently
      const now = Date.now();
      if (now - lastStateLoad.current < MIN_LOAD_INTERVAL && initialLoadPerformed.current) {
        console.log('Skipping admin state load - too recent - NO PAGE REFRESH');
        return adminState;
      }
      
      lastStateLoad.current = now;
      initialLoadPerformed.current = true;
      
      console.log('Loading admin state from service - NO PAGE REFRESH');
      const state = await loadAdminState();
      setAdminState(prevState => {
        // Only update if something actually changed
        if (JSON.stringify(prevState) !== JSON.stringify(state)) {
          console.log('Admin state changed, silently updating - NO PAGE REFRESH');
          return state;
        }
        console.log('Admin state unchanged - NO PAGE REFRESH');
        return prevState;
      });
      return state;
    } catch (error) {
      console.error('Error loading admin state:', error);
      return adminState;
    }
  }, [adminState]);
  
  // Set up polling with significantly reduced frequency
  useEffect(() => {
    // Skip if polling interval is 0 or negative
    if (pollingInterval <= 0) return;
    
    // Skip if polling is already active
    if (pollingActive.current) return;
    
    pollingActive.current = true;
    console.log('Setting up admin state polling with greatly reduced frequency - NO PAGE REFRESH:', pollingInterval);
    
    // Clear any existing timer
    if (pollingTimer.current) {
      clearInterval(pollingTimer.current);
    }
    
    // Initial load after a delay to avoid hammering the server on load
    const initialTimeout = setTimeout(() => {
      if (!initialLoadPerformed.current) {
        loadState();
      }
    }, 3000);
    
    // Listen for background polling events instead of setting up our own timer
    const handleBackgroundPoll = () => {
      console.log('Admin state background poll triggered - NO PAGE REFRESH');
      loadState();
    };
    
    window.addEventListener('background-data-poll', handleBackgroundPoll);
    
    return () => {
      pollingActive.current = false;
      clearTimeout(initialTimeout);
      window.removeEventListener('background-data-poll', handleBackgroundPoll);
      if (pollingTimer.current) {
        clearInterval(pollingTimer.current);
        pollingTimer.current = null;
      }
    };
  }, [pollingInterval, loadState]);
  
  // Handle removing an admin from active list
  const removeActiveAdmin = useCallback(async (adminName: string) => {
    try {
      console.log(`Silently removing admin ${adminName} - NO PAGE REFRESH`);
      const updatedState = await updateAdminState({
        activeAdmins: adminState.activeAdmins.filter(name => name !== adminName)
      });
      
      setAdminState(updatedState);
      return updatedState;
    } catch (error) {
      console.error('Error removing active admin:', error);
      
      // Try direct update if service fails
      try {
        const updatedActiveAdmins = adminState.activeAdmins.filter(
          (name: string) => name !== adminName
        );
        
        const updatedState = {
          ...adminState,
          activeAdmins: updatedActiveAdmins
        };
        
        setAdminState(updatedState);
        return updatedState;
      } catch (directError) {
        console.error('Direct update fallback failed:', directError);
        return adminState;
      }
    }
  }, [adminState]);
  
  return {
    adminState,
    loadState,
    removeActiveAdmin,
    usingSupabase: true
  };
};
